import BottomSheet from '@gorhom/bottom-sheet';
import CreditRequestIcon from '@assets/icons/credit-request.svg';
import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import PrintTagIcon from '@assets/icons/print-tag.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SubscriptionsIcon from '@assets/icons/subscriptions.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  AffordanceHandleIndicator,
  BottomSheetBackdrop,
} from '@/components/bottom-sheet-backdrop';
import { APP_MODAL_ROUTES, CREDIT_REQUEST_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
  VariantPackMetadata,
} from '@/contexts/item-context-types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { BRAND_PAGE_VARIANT_PACKS_VIEW_QUERY } from '@/components/brand-page/brand-page-link';
import {
  BrandPage_VariantPacksViewQuery,
  BrandPage_VariantPacksViewQueryVariables,
  CreditRequestPdpScreen_SearchVariantPacksQuery,
  CreditRequestPdpScreen_SearchVariantPacksQueryVariables,
  ItemContext_SaltItemFieldsFragment,
  ItemDetail_GtinMetadataFieldsFragment,
  ItemDetail_PurchaseHistoryQuery,
  ItemDetail_PurchaseHistoryQueryVariables,
  ItemDetail_VariantPacksQuery,
  ItemDetail_VariantPacksQueryVariables,
  ItemDetail_VariantsPacksFieldsFragment,
  Variants_Packs_Bool_Exp,
} from '@/types';
import { Button } from '@/components/button';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { DSRToolingActionSheet } from './dsr-tooling-action-sheet';
import { getVisibleQuantity } from '@/screens/new-order/hooks/use-quantity-reducer';
import { gql } from '@apollo/client';
import { ItemDetails } from './item-details';
import { ItemDetailsLarge } from './item-details-large';
import { ItemInformationConfig } from './item-detail-metadata-link';
import {
  ItemMenuOperationOption,
  ItemMenuOperationsActionSheet,
  ItemMenuOperationsRoute,
} from './item-menu-operations-action-sheet';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { RenderActionsFn } from './item-detail-actions';
import { SALT_ITEM_FIELDS_FRAGMENT } from '@/contexts/item-context-fragments';
import { SCREEN_PADDING } from '@/styles';
import { ScrollViewTabsContextProvider } from '@/contexts/scroll-view-tabs-context';
import { SEARCH_VP_FOR_CREDIT_REQUEST_ELIGIBILITY_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { setNextLayoutAnimation } from '@/utils/layout-animation';
import { StackNavigationProp } from '@react-navigation/stack';
import { ToastOptions, Type } from '@/components/toast';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useBottomSheetBackHandler } from '@/hooks/use-bottom-sheet-back-handler';
import { useCreateCreditRequest } from '@/hooks/use-create-credit-request';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import {
  useItemDetailSheetMarginTop,
  useItemDetailTitleMaxHeight,
} from '@/utils/item-details';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemLeadTimeContext } from '@/contexts/item-lead-time-context';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

// 8px of space between the bottom of header and the section title
const ADDITIONAL_SCROLL_WHITESPACE = 8;

const MENU_ICON_HITSLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export const ITEM_DETAIL_QUERY = gql`
  fragment ItemDetail_GTINMetadataFields on gtin_metadata {
    gtin
    metadata
    uuid
  }

  fragment ItemDetail_VariantsPacksFields on variants_packs {
    ...ItemsContext_VariantsPackFields
    gtin_metadata @include(if: $skipSaltApi) {
      ...ItemDetail_GTINMetadataFields
    }
    item {
      additional_information_config
      uuid
    }
    salt(locale: $locale, restaurantId: $restaurantUUID)
      @skip(if: $skipSaltApi) {
      ...ItemContext_SaltItemFields
    }
  }

  query ItemDetail_VariantPacks(
    $filters: variants_packs_bool_exp!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    visible_variant_packs(
      where: {
        variant_pack: $filters
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack {
        ...ItemDetail_VariantsPacksFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
  ${SALT_ITEM_FIELDS_FRAGMENT}
`;

export const ITEM_DETAIL_PURCHASE_HISTORY_QUERY = gql`
  fragment ItemDetail_OrderHistoryFields on GetVariantPackPurchaseHistoryResponse {
    cost_info {
      details {
        cost_in_micros
        type
      }
    }
    item_modifiers
    fulfillment_date
    order_date
    price_in_micros
    quantity
    unit
    unit_count
  }

  query ItemDetail_PurchaseHistory(
    $chatUUID: uuid!
    $itemUUID: uuid
    $variantPackUUID: uuid
    $externalItemID: String
  ) {
    getVariantPackPurchaseHistory(
      chat_id: $chatUUID
      item_id: $itemUUID
      variant_pack_id: $variantPackUUID
      external_item_id: $externalItemID
      order_by: FULFILLMENT_DATE
    ) {
      ...ItemDetail_OrderHistoryFields
    }
  }
`;

export interface Props {
  availableQuantityMap?: AvailabilityMap;
  onClose: () => void;
  onSelectItem: (item: ItemListOption, source: string) => void;
  renderActionsOverride?: RenderActionsFn;
  renderInDraggableSheet?: boolean;
  selectedItemListOption: ItemListOption;
  showToast: (options: ToastOptions) => void | string;
  source: string;
  updateQuantity: UpdateQuantityFn;
}

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.CreditRequest>,
  StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.CreateSubscription>
>;

export const ItemDetailsSheet: React.FC<Props> = ({
  availableQuantityMap,
  onClose,
  onSelectItem,
  renderActionsOverride,
  renderInDraggableSheet,
  selectedItemListOption,
  showToast,
  source,
  updateQuantity,
}) => {
  const actionSheetMarginTop = useItemDetailSheetMarginTop();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const itemDisplayOptions = useItemDisplayOptions();
  const itemQueryVariables = useItemQueryVariables();
  const maxHeaderHeight = useItemDetailTitleMaxHeight();
  const windowDimensions = useWindowDimensions();
  const { isInOrderGuide } = useOrderGuideItemsContext();
  const { variantPackHasLeadTimePolicy } = useItemLeadTimeContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const { isGuestMode, selectedRestaurantRelationship } = useUserContext();
  const [styles] = useStyles();
  const brandPagesEnabled = useDynamicConfigValue('brand_pages_enabled');
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const genericTagPrintingEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.generic_tag_printing_enabled;
  const subscriptionsEnabled = useDynamicConfigValue(
    'item_subscriptions_enabled',
  );

  const createCreditRequestFromLineItems = useCreateCreditRequest();
  const { navigate } = useNavigation<Navigation>();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showItemMenuOperationsActionSheet, setItemMenuOperationsActionSheet] =
    useState(false);

  const [
    dsrToolingActionSheetItemMetadata,
    setDSRToolingActionSheetItemMetadata,
  ] = useState<ItemListOptionMetadata | null>(null);

  const [isDSRToolingVisible, setIsDSRToolingVisible] = useState(false);

  const { displayItemCode, itemModifiersEnabled } = itemDisplayOptions;
  const queryFilters: Variants_Packs_Bool_Exp | null = useMemo(() => {
    if (!selectedItemListOption || !restaurantUUID || !supplierUUID) {
      return null;
    }

    const { externalID, itemUUID, variantPackUUID } =
      selectedItemListOption.metadata!;
    const filters: Variants_Packs_Bool_Exp = {};

    if (itemModifiersEnabled) {
      filters.item_uuid = { _eq: itemUUID };
    } else if (displayItemCode && externalID) {
      // Include external_item_id only if the supplier displays item codes
      // otherwise Hasura always returns the first item without a code
      filters._or = [
        { external_item_id: { _eq: externalID } },
        { uuid: { _eq: variantPackUUID } },
      ];
    } else {
      filters.uuid = { _eq: variantPackUUID };
    }

    return filters;
  }, [
    displayItemCode,
    itemModifiersEnabled,
    restaurantUUID,
    selectedItemListOption,
    supplierUUID,
  ]);

  const itemUUID = itemModifiersEnabled
    ? selectedItemListOption.metadata!.itemUUID
    : undefined;

  const variantPackUUID = !itemModifiersEnabled
    ? selectedItemListOption.metadata!.variantPackUUID
    : undefined;

  const { data, loading } = useWrappedQuery<
    ItemDetail_VariantPacksQuery,
    ItemDetail_VariantPacksQueryVariables
  >(ITEM_DETAIL_QUERY, {
    context: {
      anonymous: isGuestMode,
    },
    fetchPolicy: 'network-only',
    skip: !queryFilters || !restaurantUUID || !supplierUUID,
    variables: {
      ...itemQueryVariables,
      filters: queryFilters!,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const { data: orderHistoryData, loading: orderHistoryLoading } =
    useWrappedQuery<
      ItemDetail_PurchaseHistoryQuery,
      ItemDetail_PurchaseHistoryQueryVariables
    >(ITEM_DETAIL_PURCHASE_HISTORY_QUERY, {
      context: {
        noBatch: true,
      },
      skip: isGuestMode,
      variables: {
        chatUUID: selectedRestaurantRelationship.chatUUIDs[0],
        externalItemID: selectedItemListOption.metadata?.externalID,
        itemUUID,
        variantPackUUID,
      },
    });

  const orderHistoryItems =
    orderHistoryData?.getVariantPackPurchaseHistory ?? [];

  const { data: searchVPForCreditRequestEligibilityData } = useWrappedQuery<
    CreditRequestPdpScreen_SearchVariantPacksQuery,
    CreditRequestPdpScreen_SearchVariantPacksQueryVariables
  >(SEARCH_VP_FOR_CREDIT_REQUEST_ELIGIBILITY_QUERY, {
    skip:
      isGuestMode ||
      !selectedRestaurantRelationship?.showCreditRequest ||
      !selectedItemListOption.metadata!.variantPackUUID ||
      !restaurantUUID ||
      !supplierUUID,
    variables: {
      code: '', // because we are using variantPackUUID
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
      variantPackUUID: selectedItemListOption.metadata!.variantPackUUID,
    },
  });

  const creditRequestEligibilityData =
    searchVPForCreditRequestEligibilityData
      ?.searchVariantPacksForCreditRequest[0];

  const { data: brandPageData, loading: brandPageLoading } = useWrappedQuery<
    BrandPage_VariantPacksViewQuery,
    BrandPage_VariantPacksViewQueryVariables
  >(BRAND_PAGE_VARIANT_PACKS_VIEW_QUERY, {
    skip: isGuestMode || !brandPagesEnabled || !restaurantUUID || !supplierUUID,
    variables: {
      variantPackUUID: selectedItemListOption.metadata!.variantPackUUID,
    },
  });
  const brandPageDetails =
    brandPageData?.brand_page_variant_packs[0]?.brand_page;

  const variantPacks: ItemDetail_VariantsPacksFieldsFragment[] = useMemo(() => {
    return (data?.visible_variant_packs || []).reduce<
      ItemDetail_VariantsPacksFieldsFragment[]
    >((acc, vvp) => {
      const vp = vvp.variant_pack;
      if (vp) {
        acc.push(vp);
      }
      return acc;
    }, []);
  }, [data]);

  const variantPackUUIDs: string[] = useMemo(
    () => variantPacks.map((vp) => vp.uuid),
    [variantPacks],
  );
  const skuQuantityState = useItemQuantityState(variantPackUUIDs);

  const selectedItem = useMemo(() => {
    if (!selectedItemListOption) {
      return null;
    }
    // Lead with the variant pack the sheet was opened with so it becomes the group's representative: the photo fallback below reads its photos when the caller has none, and without item codes to group by it is also the entry this picks.
    const sortedVariantPacks = variantPacks.reduce<
      ItemDetail_VariantsPacksFieldsFragment[]
    >((acc, vp) => {
      if (vp.uuid === selectedItemListOption.metadata?.variantPackUUID) {
        acc.unshift(vp);
      } else {
        acc.push(vp);
      }

      return acc;
    }, []);

    const grouped =
      adaptAndGroupVariantPacks(
        sortedVariantPacks,
        selectedItemListOption.metadata?.promotion?.uuid,
      )[0] || null;

    const displayPhotoURLs = selectedItemListOption.metadata?.photoURLs;
    if (!grouped?.metadata || !displayPhotoURLs?.length) {
      return grouped;
    }

    return {
      ...grouped,
      metadata: { ...grouped.metadata, photoURLs: displayPhotoURLs },
    };
  }, [adaptAndGroupVariantPacks, variantPacks, selectedItemListOption]);

  const isItemAllowedForSubscription =
    selectedItem?.metadata &&
    !selectedItem.metadata.leadTimeDays &&
    !selectedItem.metadata.leadTimeCutoffTime &&
    !variantPackHasLeadTimePolicy(selectedItem.metadata.variantPackUUID) &&
    !selectedItem.metadata.prebookConfig;

  const itemInformation = useMemo(() => {
    if (!selectedItemListOption) {
      return null;
    }
    const vpWithConfig = variantPacks.find((vp) => {
      const config = vp.item
        .additional_information_config as ItemInformationConfig;

      if (!config || !config.additional_information) {
        return false;
      }

      return (
        config.additional_information.additional_information_button
          ?.display_text &&
        config.additional_information.additional_information_button.url
      );
    });

    if (!vpWithConfig) {
      return null;
    }

    return vpWithConfig.item
      .additional_information_config as ItemInformationConfig;
  }, [selectedItemListOption, variantPacks]);

  const [firstVariantPackGTINMetadata, firstVariantPackMetadata] =
    useMemo(() => {
      const orderedSkus = selectedItem?.metadata?.skus;
      if (!orderedSkus?.length) {
        return [null, null];
      }

      const variantPackToGTINMetadataMap: {
        [variantPackUUID: string]: ItemDetail_GtinMetadataFieldsFragment;
      } = {};
      const variantPackToMetadataMap: {
        [variantPackUUID: string]: VariantPackMetadata;
      } = {};
      for (const vp of variantPacks) {
        const gtinMetadata = vp.gtin_metadata?.[0];
        if (gtinMetadata) {
          variantPackToGTINMetadataMap[vp.uuid] = gtinMetadata;
        }

        if (vp.metadata) {
          variantPackToMetadataMap[vp.uuid] = vp.metadata;
        }
      }

      let gtinMetadata: ItemDetail_GtinMetadataFieldsFragment | null = null;
      let metadata: VariantPackMetadata | null = null;
      for (let sku of orderedSkus) {
        if (
          !gtinMetadata &&
          variantPackToGTINMetadataMap[sku.variantPackUUID]
        ) {
          gtinMetadata = variantPackToGTINMetadataMap[sku.variantPackUUID];
        }

        if (!metadata && variantPackToMetadataMap[sku.variantPackUUID]) {
          metadata = variantPackToMetadataMap[sku.variantPackUUID];
        }

        if (gtinMetadata && metadata) {
          break;
        }
      }

      return [gtinMetadata, metadata];
    }, [variantPacks, selectedItem]);

  const firstSaltItemInfo = useMemo(() => {
    const orderedSkus = selectedItem?.metadata?.skus;
    if (!saltApiIntegrationEnabled || !orderedSkus?.length) {
      return null;
    }

    const variantPackToSaltMetadataMap: {
      [variantPackUUID: string]: ItemContext_SaltItemFieldsFragment;
    } = {};
    for (const vp of variantPacks) {
      if (vp.salt) {
        variantPackToSaltMetadataMap[vp.uuid] = vp.salt;
      }
    }

    for (let sku of orderedSkus) {
      if (variantPackToSaltMetadataMap[sku.variantPackUUID]) {
        return variantPackToSaltMetadataMap[sku.variantPackUUID];
      }
    }

    return null;
  }, [saltApiIntegrationEnabled, selectedItem, variantPacks]);

  // Absolutely positioned header takes up maxHeaderHeight - actionSheetMarginTop space.
  // Add additional 8px of space so titles render 8px below the bottom of the header.
  const scrollOffset =
    maxHeaderHeight - actionSheetMarginTop + ADDITIONAL_SCROLL_WHITESPACE;

  const isSkuInCartOrOrderGuide = useCallback(
    (sku: ItemListOptionMetadata) =>
      Boolean(
        getVisibleQuantity(skuQuantityState, sku.variantPackUUID) ||
          isInOrderGuide(sku.variantPackUUID),
      ),
    [isInOrderGuide, skuQuantityState],
  );

  // Resets showSelector state after leaving an item's detail seelctor
  // so each item details opens to beginning of selector UI flow.
  // Show selector immediately if item has mods and is not in cart or order guide.
  useEffect(
    () =>
      setShowSelector(
        Boolean(
          !selectedItem?.metadata?.skus.some(isSkuInCartOrOrderGuide) &&
            selectedItem?.metadata?.modifierGroups?.length,
        ),
      ),
    [selectedItem, isSkuInCartOrOrderGuide],
  );

  useEffect(() => {
    if (renderInDraggableSheet) {
      if (selectedItemListOption) {
        bottomSheetRef.current?.expand();
      } else {
        bottomSheetRef.current?.close();
      }
    }
  }, [selectedItemListOption, renderInDraggableSheet]);

  const itemMenuOperations: ItemMenuOperationOption[] = useMemo(() => {
    const subscriptionOption = {
      metadata: {
        Icon: SubscriptionsIcon,
      },
      title: i18n.t('create_subscription'),
      value: ItemMenuOperationsRoute.CreateSubscription,
    };
    if (Platform.OS === 'web') {
      if (subscriptionsEnabled && isItemAllowedForSubscription) {
        return [subscriptionOption];
      }
      return [];
    }

    const options = [];

    if (subscriptionsEnabled && isItemAllowedForSubscription) {
      options.push(subscriptionOption);
    }

    if (
      selectedRestaurantRelationship?.showCreditRequest &&
      creditRequestEligibilityData?.eligible_for_credit
    ) {
      options.push({
        metadata: {
          Icon: CreditRequestIcon,
        },
        title: i18n.t('credit_request'),
        value: ItemMenuOperationsRoute.CreditRequest,
      });
    }
    if (
      selectedRestaurantRelationship?.tagPrintingEnabled ||
      genericTagPrintingEnabled
    ) {
      options.push({
        metadata: {
          Icon: PrintTagIcon,
        },
        title: i18n.t('print_tag'),
        value: ItemMenuOperationsRoute.TagPrinting,
      });
    }

    return options;
  }, [
    creditRequestEligibilityData?.eligible_for_credit,
    genericTagPrintingEnabled,
    isItemAllowedForSubscription,
    selectedRestaurantRelationship?.showCreditRequest,
    selectedRestaurantRelationship?.tagPrintingEnabled,
    subscriptionsEnabled,
  ]);

  const { handleSheetPositionChange } =
    useBottomSheetBackHandler(bottomSheetRef);

  const ItemMenuButton =
    !isGuestMode && itemMenuOperations.length > 0 ? (
      <Button
        hitSlop={MENU_ICON_HITSLOP}
        Icon={MenuHorizontalIcon}
        onPress={() => {
          setItemMenuOperationsActionSheet(true);
        }}
        size="small"
        style={[
          styles.menuButton,
          isSmallLayout ? styles.menuButtonSmall : null,
        ]}
        testID="item_detail-menu-button"
        trackingData={{
          name: 'item_detail.menu_button',
          properties: {
            itemUUID: selectedItem?.metadata?.itemUUID,
          },
        }}
        type="tertiary"
      />
    ) : null;

  const renderAsLargeLayout =
    isLargeLayout && !selectedRestaurantRelationship.largeScreenModalHoldout;

  const ItemDetailComponent = renderAsLargeLayout
    ? ItemDetailsLarge
    : ItemDetails;

  const content =
    loading || brandPageLoading ? (
      <View style={styles.staticContentContainer}>
        <ActivityIndicator />
      </View>
    ) : selectedItem?.metadata ? (
      <ScrollViewTabsContextProvider scrollOffset={scrollOffset}>
        <ItemDetailComponent
          availableQuantityMap={availableQuantityMap}
          brandPageDetails={brandPageDetails}
          gtinMetadata={firstVariantPackGTINMetadata}
          inDraggableSheet={renderInDraggableSheet}
          isSkuInCartOrOrderGuide={isSkuInCartOrOrderGuide}
          itemInformation={itemInformation}
          ItemMenuButton={ItemMenuButton}
          onChangeSelector={() => {
            setShowSelector((show) => !show);
            setNextLayoutAnimation();
          }}
          onClose={onClose}
          onSelectItem={onSelectItem}
          orderHistoryItems={orderHistoryItems}
          orderHistoryLoading={orderHistoryLoading}
          renderActions={renderActionsOverride}
          saltItemInfo={firstSaltItemInfo}
          selectedItem={selectedItem}
          setDSRToolingActionSheetItemMetadata={
            setDSRToolingActionSheetItemMetadata
          }
          setShowTitle={setShowTitle}
          showSelector={showSelector}
          source={source}
          updateQuantity={updateQuantity}
          variantPackMetadata={firstVariantPackMetadata}
        />
        {showItemMenuOperationsActionSheet ? (
          <ItemMenuOperationsActionSheet
            onClose={() => {
              setItemMenuOperationsActionSheet(false);
            }}
            onNavigateToCreateSubscriptionScreen={(variantPackUUID) => {
              setItemMenuOperationsActionSheet(false);
              const variantPackMetadata = selectedItem?.metadata?.skus.find(
                (sku) => sku.variantPackUUID === variantPackUUID,
              );
              if (!variantPackMetadata) {
                return;
              }
              navigate(APP_MODAL_ROUTES.CreateSubscription, {
                metadata: variantPackMetadata,
                onViewClick: onClose,
                reopenItemMenuOperations: () =>
                  setItemMenuOperationsActionSheet(true),
              });
              if (!isSmallLayout) {
                onClose();
              }
            }}
            onNavigateToCreditRequestScreen={() => {
              setItemMenuOperationsActionSheet(false);
              navigate(APP_MODAL_ROUTES.CreditRequest, {
                params: {
                  confirmOnExit: false,
                  eligibleForCredit:
                    creditRequestEligibilityData?.eligible_for_credit,
                  guaranteed: creditRequestEligibilityData?.guaranteed,
                  itemDetail: selectedItemListOption,
                  onClose: () => setItemMenuOperationsActionSheet(true),
                  onSuccess: async (
                    creditRequestBatchLineItem: CreditRequestBatchLineItem,
                  ) => {
                    try {
                      await createCreditRequestFromLineItems([
                        creditRequestBatchLineItem,
                      ]);
                      showToast({
                        content: i18n.t('credit_request.toast.success'),
                        type: Type.POSITIVE,
                      });
                    } catch (err) {
                      showToast({
                        content:
                          err instanceof Error
                            ? err.message
                            : i18n.t('toast.standard_error'),
                        type: Type.NEGATIVE,
                      });
                    }
                  },
                },
                screen: CREDIT_REQUEST_ROUTES.Base,
              });

              if (!isSmallLayout) {
                onClose();
              }
            }}
            operations={itemMenuOperations}
            selectedItem={selectedItem}
            variantPackUUIDs={variantPackUUIDs}
          />
        ) : null}
      </ScrollViewTabsContextProvider>
    ) : null;

  // Max height of the action sheet is 90% of the screen to enable scrolling
  // + still have room to dismiss the modal
  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.9);
  // Min height of the action sheet is 1/3 the height of the screen to
  // ensure loading state is visible and prevent drastic differences in height
  // after content loads
  const actionSheetMinHeight = Math.floor(windowDimensions.height / 3);

  return (
    <>
      {isDSRToolingVisible && dsrToolingActionSheetItemMetadata ? (
        <DSRToolingActionSheet
          itemMetadata={dsrToolingActionSheetItemMetadata}
          onClose={() => {
            setDSRToolingActionSheetItemMetadata(null);
          }}
          updateQuantity={updateQuantity}
        />
      ) : null}
      {renderInDraggableSheet ? (
        <BottomSheet
          backdropComponent={BottomSheetBackdrop}
          enablePanDownToClose
          handleComponent={AffordanceHandleIndicator}
          keyboardBehavior="extend"
          keyboardBlurBehavior="restore"
          onChange={handleSheetPositionChange}
          onClose={onClose}
          ref={bottomSheetRef}
          snapPoints={
            (selectedItem?.metadata?.photoURLs || []).length
              ? ['65%', '90%']
              : ['50%', '90%']
          }
        >
          {content}
        </BottomSheet>
      ) : (
        <BaseActionSheet
          additionalAction={ItemMenuButton}
          contentStyle={[
            styles.actionSheetContent,
            {
              maxHeight: actionSheetMaxHeight,
              minHeight: actionSheetMinHeight,
            },
            isSmallLayout ? null : styles.borderRadiusReset,
          ]}
          isVisible={
            Boolean(selectedItemListOption) &&
            !dsrToolingActionSheetItemMetadata
          }
          modalHeaderStyle={
            renderAsLargeLayout
              ? styles.actionSheetHeaderLarge
              : styles.actionSheetHeader
          }
          modalStyle={[
            Platform.OS === 'web' ? webStyles.actionSheetModalWeb : null,
            renderAsLargeLayout
              ? {
                  maxWidth: Math.floor(windowDimensions.width * 0.85),
                }
              : null,
          ]}
          onClose={onClose}
          onModalBackArrowPress={
            showSelector ? () => setShowSelector(false) : undefined
          }
          title={showTitle ? selectedItemListOption?.title : undefined}
          {...(renderAsLargeLayout
            ? { onModalBackArrowPress: undefined }
            : null)}
          onModalHide={() => {
            setIsDSRToolingVisible(true);
          }}
          onModalShow={() => {
            setIsDSRToolingVisible(false);
          }}
        >
          {content}
        </BaseActionSheet>
      )}
    </>
  );
};

const webStyles = StyleSheet.create({
  actionSheetModalWeb: {
    // ensure only scrollable item on web modal is the scroll view
    // @ts-ignore
    overflowY: 'hidden',
  },
});

const useStyles = createStyles(() => ({
  actionSheetContent: {
    overflow: 'hidden',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
  },
  actionSheetHeader: {
    paddingBottom: 0,
  },
  actionSheetHeaderLarge: {
    display: 'none',
  },
  borderRadiusReset: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  menuButton: {
    alignSelf: 'flex-end',
    height: 24,
  },
  menuButtonSmall: {
    marginRight: 8,
  },
  staticContentContainer: {
    justifyContent: 'flex-start',
    paddingVertical: SCREEN_PADDING,
  },
}));
